import { fetchAuthSession } from "aws-amplify/auth";

interface TokenInfo {
  token: string;
  expiration: Date;
}

// Token cache configuration
const TOKEN_CACHE_KEY = "atomic_auth_tokens";
const TOKEN_CACHE_TTL = 55 * 60 * 1000; // 55 minutes (tokens expire at 60min)

interface CachedTokens {
  idToken: string;
  accessToken: string;
  timestamp: number;
  expiration: number; // Token expiration timestamp in ms
}

/**
 * Authentication Service - JWT Implementation
 *
 * Responsibilities:
 * 1. Store and manage JWT ID token
 * 2. Schedule automatic token refresh
 * 3. Provide JWT token for API calls
 * 4. Handle network errors gracefully
 * 5. Cache tokens in localStorage for faster subsequent page loads
 *
 * Key Principles:
 * - Single source of truth for token state
 * - Clear separation of concerns
 * - Graceful error handling
 * - No circular dependencies
 */
export class AuthenticationService {
  private static instance: AuthenticationService;

  // Token state
  private static idToken: string | undefined;
  private static accessToken: string | undefined;
  private static tokenInfo: TokenInfo | null = null;
  private static tokenRefreshTimer: NodeJS.Timeout | null = null;

  // Control flags
  private static authenticationFailed: boolean = false;
  private static currentTokenRefreshPromise: Promise<string | null> | null = null;
  private static onTokenRefresh: ((token: string) => void) | null = null;
  private static visibilityChangeListenerAdded: boolean = false;

  // Constants
  private static readonly TOKEN_BUFFER_MINUTES = 2;

  private constructor() {}

  public static getInstance(): AuthenticationService {
    if (!AuthenticationService.instance) {
      AuthenticationService.instance = new AuthenticationService();
      AuthenticationService.setupVisibilityChangeListener();
      // Try to restore tokens from localStorage cache on init
      AuthenticationService.restoreFromCache();
    }
    return AuthenticationService.instance;
  }

  /**
   * PERF: Restore tokens from localStorage cache for faster subsequent page loads
   * Expected improvement: 500-1000ms on page refresh
   */
  private static restoreFromCache(): boolean {
    try {
      const cached = localStorage.getItem(TOKEN_CACHE_KEY);
      if (!cached) return false;

      const { idToken, accessToken, timestamp, expiration }: CachedTokens = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is still valid (within TTL and token not expired)
      if (
        now - timestamp > TOKEN_CACHE_TTL ||
        now > expiration - AuthenticationService.TOKEN_BUFFER_MINUTES * 60 * 1000
      ) {
        localStorage.removeItem(TOKEN_CACHE_KEY);
        return false;
      }

      // Restore tokens to memory
      AuthenticationService.idToken = idToken;
      AuthenticationService.accessToken = accessToken;
      AuthenticationService.tokenInfo = {
        token: idToken,
        expiration: new Date(expiration),
      };

      // Schedule refresh based on cached expiration
      const refreshTime = new Date(
        expiration - AuthenticationService.TOKEN_BUFFER_MINUTES * 60 * 1000
      );
      const delay = Math.max(0, refreshTime.getTime() - now);
      AuthenticationService.tokenRefreshTimer = setTimeout(() => {
        AuthenticationService.refreshToken();
      }, delay);

      console.log("[Auth] Restored tokens from cache");
      return true;
    } catch (error) {
      console.warn("[Auth] Failed to restore tokens from cache:", error);
      localStorage.removeItem(TOKEN_CACHE_KEY);
      return false;
    }
  }

  /**
   * Save tokens to localStorage cache
   */
  private static saveToCache() {
    try {
      if (
        !AuthenticationService.idToken ||
        !AuthenticationService.accessToken ||
        !AuthenticationService.tokenInfo
      ) {
        return;
      }

      const cacheData: CachedTokens = {
        idToken: AuthenticationService.idToken,
        accessToken: AuthenticationService.accessToken,
        timestamp: Date.now(),
        expiration: AuthenticationService.tokenInfo.expiration.getTime(),
      };

      localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn("[Auth] Failed to save tokens to cache:", error);
    }
  }

  /**
   * Clear token cache from localStorage
   */
  private static clearCache() {
    try {
      localStorage.removeItem(TOKEN_CACHE_KEY);
    } catch {
      // Ignore localStorage errors
    }
  }

  /**
   * Get cached tokens without network call (for fast initial auth check)
   */
  public static getCachedTokens(): { idToken: string; accessToken: string } | null {
    // First check in-memory
    if (AuthenticationService.idToken && AuthenticationService.accessToken) {
      return {
        idToken: AuthenticationService.idToken,
        accessToken: AuthenticationService.accessToken,
      };
    }

    // Try to restore from localStorage
    if (AuthenticationService.restoreFromCache()) {
      return {
        idToken: AuthenticationService.idToken!,
        accessToken: AuthenticationService.accessToken!,
      };
    }

    return null;
  }

  /**
   * Set ID token and reset all authentication state
   * This is the primary entry point for authentication
   */
  public static setIdToken(idToken: string | undefined) {
    // Clear existing state
    AuthenticationService.clearAllState();

    if (idToken) {
      // Set new token
      AuthenticationService.idToken = idToken;
      AuthenticationService.authenticationFailed = false;

      // Parse and schedule refresh
      AuthenticationService.parseTokenAndScheduleRefresh(idToken);
    }
  }

  /**
   * Set access token for API Gateway authentication
   */
  public static setAccessToken(accessToken: string | undefined) {
    AuthenticationService.accessToken = accessToken;
  }

  /**
   * Get current access token
   * Used for API Gateway HTTP V2 JWT authorizer
   */
  public static getAccessToken() {
    return AuthenticationService.accessToken;
  }

  /**
   * Set callback for token refresh events
   */
  public static setOnTokenRefresh(callback: (token: string) => void) {
    AuthenticationService.onTokenRefresh = callback;
  }

  /**
   * Get current ID token
   */
  public static getIdToken() {
    return AuthenticationService.idToken;
  }

  /**
   * Clear all authentication state and local token cache.
   * Called on logout to prevent stale tokens on next visit.
   */
  public static logout() {
    AuthenticationService.clearAllState();
  }

  private static clearAllState() {
    // Clear token state
    AuthenticationService.idToken = undefined;
    AuthenticationService.accessToken = undefined;
    AuthenticationService.tokenInfo = null;
    AuthenticationService.authenticationFailed = false;

    // Clear refresh state
    AuthenticationService.currentTokenRefreshPromise = null;
    if (AuthenticationService.tokenRefreshTimer) {
      clearTimeout(AuthenticationService.tokenRefreshTimer);
      AuthenticationService.tokenRefreshTimer = null;
    }

    // Clear localStorage cache
    AuthenticationService.clearCache();
  }

  /**
   * Parse token and schedule automatic refresh
   */
  private static parseTokenAndScheduleRefresh(token: string) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiration = new Date(payload.exp * 1000);

      AuthenticationService.tokenInfo = {
        token,
        expiration,
      };

      // Schedule refresh before expiration
      const now = new Date();
      const refreshTime = new Date(
        expiration.getTime() - AuthenticationService.TOKEN_BUFFER_MINUTES * 60 * 1000
      );
      const delay = Math.max(0, refreshTime.getTime() - now.getTime());

      AuthenticationService.tokenRefreshTimer = setTimeout(() => {
        AuthenticationService.refreshToken();
      }, delay);

      // Save to localStorage cache for faster subsequent page loads
      AuthenticationService.saveToCache();
    } catch (error) {
      console.error("Failed to parse token expiration:", error);
    }
  }

  /**
   * Refresh token - only called when needed
   */
  private static async refreshToken(): Promise<string | null> {
    // Prevent multiple simultaneous refreshes
    if (AuthenticationService.currentTokenRefreshPromise) {
      return await AuthenticationService.currentTokenRefreshPromise;
    }

    AuthenticationService.currentTokenRefreshPromise = AuthenticationService.performTokenRefresh();
    const result = await AuthenticationService.currentTokenRefreshPromise;
    AuthenticationService.currentTokenRefreshPromise = null;
    return result;
  }

  /**
   * Perform actual token refresh
   */
  private static async performTokenRefresh(): Promise<string | null> {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const newIdToken = session.tokens?.idToken?.toString();
      const newAccessToken = session.tokens?.accessToken?.toString();

      if (newIdToken) {
        // Update tokens and reschedule
        AuthenticationService.idToken = newIdToken;
        if (newAccessToken) {
          AuthenticationService.accessToken = newAccessToken;
        }
        AuthenticationService.parseTokenAndScheduleRefresh(newIdToken);

        // Notify listeners
        if (AuthenticationService.onTokenRefresh) {
          AuthenticationService.onTokenRefresh(newIdToken);
        }

        return newIdToken;
      } else {
        console.warn("No new token received from refresh");
        return null;
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      AuthenticationService.authenticationFailed = true;
      return null;
    }
  }

  /**
   * Setup visibility change listener for token refresh
   */
  private static setupVisibilityChangeListener() {
    if (AuthenticationService.visibilityChangeListenerAdded) {
      return;
    }

    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        // Only refresh if no refresh is in progress and token is near expiry
        if (!AuthenticationService.currentTokenRefreshPromise) {
          const tokenInfo = AuthenticationService.tokenInfo;
          const now = new Date();
          const bufferMs = AuthenticationService.TOKEN_BUFFER_MINUTES * 60 * 1000;
          const isExpiredOrNear =
            !tokenInfo || tokenInfo.expiration.getTime() - now.getTime() <= bufferMs;

          if (isExpiredOrNear) {
            try {
              await AuthenticationService.refreshToken();
            } catch (error) {
              console.warn("Token refresh failed on visibility change", error);
            }
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    AuthenticationService.visibilityChangeListenerAdded = true;
  }

  /**
   * Check if response indicates authentication failure
   */
  private async isAuthenticationError(response: Response): Promise<boolean> {
    // Check for common authentication error status codes
    if (response.status === 401 || response.status === 403) {
      return true;
    }

    // Check for GraphQL errors in the response body
    try {
      const responseText = await response.text();
      const responseData = JSON.parse(responseText);

      if (responseData.errors && Array.isArray(responseData.errors)) {
        const authErrorMessages = [
          "User not found",
          "Unauthorized",
          "Forbidden",
          "Authentication failed",
          "Invalid token",
          "Token expired",
        ];

        for (const error of responseData.errors) {
          if (
            error.message &&
            authErrorMessages.some((authMsg) =>
              error.message.toLowerCase().includes(authMsg.toLowerCase())
            )
          ) {
            return true;
          }
        }
      }
    } catch {
      // If we can't parse the response as JSON, it's not a GraphQL error
      // Silently ignore parsing errors
    }

    return false;
  }

  /**
   * Make authenticated API requests with JWT token
   */
  makeAuthenticatedRequest = async (
    uri: RequestInfo | URL,
    init?: RequestInit | undefined,
    retryCount: number = 0
  ): Promise<Response> => {
    const MAX_RETRIES = 2;

    // If authentication has previously failed, don't execute until setIdToken is called
    if (AuthenticationService.authenticationFailed) {
      return new Response(
        JSON.stringify({
          data: null,
          errors: [{ message: "Authentication missing" }],
        }),
        { status: 401, headers: { "Content-Type": "application/json" } }
      );
    }

    // Ensure we have a valid token
    if (!AuthenticationService.idToken) {
      console.warn("makeAuthenticatedRequest: no ID token available");
      return new Response(
        JSON.stringify({
          data: null,
          errors: [{ message: "Authentication missing" }],
        }),
        { status: 401, headers: { "Content-Type": "application/json" } }
      );
    }

    // Check if token needs refresh
    const tokenInfo = AuthenticationService.tokenInfo;
    const now = new Date();
    const bufferMs = AuthenticationService.TOKEN_BUFFER_MINUTES * 60 * 1000;
    const isExpiredOrNear =
      !tokenInfo || tokenInfo.expiration.getTime() - now.getTime() <= bufferMs;

    if (isExpiredOrNear) {
      try {
        await AuthenticationService.refreshToken();
      } catch (error) {
        console.warn("Token refresh failed before request", error);
        return new Response(
          JSON.stringify({
            data: null,
            errors: [{ message: "Authentication failed" }],
          }),
          { status: 401, headers: { "Content-Type": "application/json" } }
        );
      }
    }

    // Build request with JWT token
    const requestOptions: RequestInit = {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${AuthenticationService.getIdToken()}`,
        ...init?.headers,
      },
    };

    try {
      const response = await fetch(uri, requestOptions);

      // Check if the response indicates an authentication failure
      const responseClone = response.clone();
      if (await this.isAuthenticationError(responseClone)) {
        console.warn(
          `makeAuthenticatedRequest: authentication failed (attempt ${
            retryCount + 1
          }/${MAX_RETRIES + 1})`
        );

        // If we haven't exceeded max retries, try to refresh token and retry
        if (retryCount < MAX_RETRIES) {
          const newToken = await AuthenticationService.refreshToken();

          if (newToken) {
            // Retry the request with the new token
            return await this.makeAuthenticatedRequest(uri, init, retryCount + 1);
          } else {
            console.error("Token refresh failed, marking authentication as failed");
            AuthenticationService.authenticationFailed = true;
          }
        } else {
          console.error("Max retries exceeded, marking authentication as failed");
          AuthenticationService.authenticationFailed = true;
        }
      }

      return response;
    } catch (error) {
      console.error(
        `makeAuthenticatedRequest: fetch failed (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`,
        error
      );

      // For network errors, return a graceful response instead of throwing
      if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
        console.warn("Network error detected, returning graceful offline response");
        return new Response(
          JSON.stringify({
            data: null,
            errors: [
              {
                message: "Network unavailable. Please check your connection and try again.",
              },
            ],
          }),
          { status: 200, headers: { "Content-Type": "application/json" } }
        );
      }

      // Only mark as authentication failed if we've exhausted retries and it's not a network error
      if (retryCount >= MAX_RETRIES) {
        AuthenticationService.authenticationFailed = true;
      }

      throw error;
    }
  };
}
